/**
 * LoginPage.tsx
 *
 * Staff login — username + password form.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { buildCorporateLoginUrl } from '@/lib/corpAuth';
import { consumeLogoutReason } from '@/lib/session';
import { mapStaffAuthUser } from '@/lib/mapStaffAuthUser';
import { useUiLang, type UiLang } from '@/lib/uiLanguage';
import type { SessionEndReason } from '@/types/auth.types';
import { getLegacyOrigin } from '@/lib/appStage';

/**
 * Why the previous session ended. The inactivity wording is legacy's exact
 * string (authenticateTouchMd.php redirected with "Logged out due to
 * inactivity"), so staff moving between the two apps read the same thing.
 */
const SESSION_END_MESSAGES: Record<SessionEndReason, string> = {
  idle: 'Logged out due to inactivity',
  expired: 'Your session ended. Please sign in again.',
  restricted: 'Access from your current network is not permitted for your account.',
  user: 'You have been signed out.',
};

const DEMO_LOGIN_NAME = 'nexthealth';
const PROSPERA_LOGO_URL = `${getLegacyOrigin()}/uimages/img8/brandlogo.png`;
const TERMS_URL = 'https://prospera.ai/terms';
const PRIVACY_URL = '/privacy';

const CONSENT_COPY: Record<UiLang, { before: string; terms: string; between: string; privacy: string; after: string }> = {
  en: {
    before: 'By signing in, you agree to our ',
    terms: 'Terms & Conditions',
    between: ' and ',
    privacy: 'Privacy Policy',
    after: '.',
  },
  fr: {
    before: 'En vous connectant, vous acceptez nos ',
    terms: "Conditions d'utilisation",
    between: ' et notre ',
    privacy: 'Politique de confidentialité',
    after: '.',
  },
};

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const storeLogin = useAuthStore((s) => s.login);
  const consent = CONSENT_COPY[useUiLang()];

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const [loginName] = useState(DEMO_LOGIN_NAME);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  // Break-glass: /login?form=1 keeps the legacy username/password form reachable
  // even after the corp auto-redirect is live (also the recovery target when a
  // corp code exchange fails, see CorporateAuthCallbackPage).
  const formMode = searchParams.get('form') === '1';

  // Why the previous session ended, if it ended rather than never existing.
  // Read once on mount: consumeLogoutReason() clears the sessionStorage copy so
  // the message doesn't resurface on a refresh.
  const [endReason] = useState(() => consumeLogoutReason());

  // A session that was ended (sign-out, idle) gets the same corp URL
  // SessionWatcher redirects to — account list forced, landing on home rather
  // than the previous user's last screen — so it doesn't matter which of the two
  // redirects wins. Otherwise return the user to where they were headed.
  const corporateLoginUrl = endReason
    ? buildCorporateLoginUrl({ forceAccountSelection: true })
    : buildCorporateLoginUrl({ next: from });

  // Strip ?reason= from the URL so it isn't bookmarkable or shareable and doesn't
  // reappear on reload — the state above already holds it.
  useEffect(() => {
    if (!searchParams.get('reason')) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('reason');
    window.history.replaceState({}, '', url.toString());
  }, [searchParams]);

  // The direct login page is deprecated and the corp app is the only way to sign
  // in: when it is configured and we aren't in break-glass mode, send the user
  // straight there — with or without a reason for the previous session ending.
  // replace() keeps /login out of history so the back button won't bounce back
  // here. The reason banner below shows only on the break-glass form and in dev.
  const shouldRedirectToCorp = Boolean(corporateLoginUrl) && !formMode;

  useEffect(() => {
    if (shouldRedirectToCorp && corporateLoginUrl) {
      window.location.replace(corporateLoginUrl);
    }
  }, [shouldRedirectToCorp, corporateLoginUrl]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const resp = await authApi.login({
        userName: username.trim(),
        password,
        loginName: loginName.trim(),
      });
      // The fifth argument is `ownerAgreementHold: boolean` — see authStore's
      // login(). It used to be passed `resp.session`, the idle-logout window,
      // which is an OBJECT and therefore always truthy: every form sign-in was
      // diverted by RequireAuth to /owner-agreement-required, a page that
      // renders blank, and nobody could get in. The corp-SSO path
      // (CorporateAuthCallbackPage) passes the right value, which is why only
      // the break-glass form was affected and why it went unnoticed.
      //
      // TypeScript could not catch it: `session` is not declared on
      // AuthResponse, so `resp.session` is `any`, and `any` is assignable to
      // `boolean`. The type error on that line was hiding the runtime bug.
      //
      // `/auth/staff/login` does not return ownerAgreementHold at all — the API
      // only computes it for corp sessions (StaffAuthController, guarded on
      // masterUserId), so false is the honest value here. Wiring `session`
      // through is a separate change, because it switches on idle auto-logout.
      storeLogin(
        resp.token,
        mapStaffAuthUser(resp.user),
        resp.pendingDisclaimers ?? [],
        resp.modules ?? null,
        false,
      );
      // RequireAuth will divert to /disclaimer when the queue is non-empty.
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(
        (err as { userMessage?: string })?.userMessage ?? 'Invalid credentials. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }

  if (shouldRedirectToCorp) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#040816] px-4 py-10 text-white">
        <p className="text-sm text-slate-400">Redirecting to sign in…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#040816] px-4 py-10 text-white">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <img
            src={PROSPERA_LOGO_URL}
            alt="Prospera"
            className="h-auto w-full max-w-[280px] object-contain"
          />
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#141a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)] space-y-5">
          {endReason && (
            <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
              {SESSION_END_MESSAGES[endReason]}
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="hidden" name="loginName" value={loginName} />

            <div>
              <label htmlFor="username" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#6558f5] focus:outline-none focus:ring-2 focus:ring-[#6558f5]/40"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 pr-10 text-sm text-white placeholder:text-slate-500 focus:border-[#6558f5] focus:outline-none focus:ring-2 focus:ring-[#6558f5]/40"
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  tabIndex={-1}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !loginName || !username || !password}
              className="w-full rounded-md bg-[#6558f5] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#766af8] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            <p className="text-center text-xs leading-relaxed text-slate-500">
              {consent.before}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[#9088f8] hover:text-[#b3aafa]"
              >
                {consent.terms}
              </a>
              {consent.between}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[#9088f8] hover:text-[#b3aafa]"
              >
                {consent.privacy}
              </a>
              {consent.after}
            </p>
          </form>

          {corporateLoginUrl ? (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <span className="bg-[#141a2b] px-2">or continue with</span>
                </div>
              </div>

              <a
                href={corporateLoginUrl}
                className="block w-full rounded-md border border-white/10 bg-white px-4 py-2.5 text-center text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100"
              >
                Sign in with Corporate Login
              </a>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
